import { useState } from "react";
import type { RecurrenceRule } from "../../../../shared/types";
import { getToday } from "../../../../shared/types/date-helpers";
import { CustomSelect } from "../../../../ui/atoms/CustomSelect";

const DAY_LABELS = ["D", "L", "M", "M", "J", "V", "S"] as const;

interface RecurrencePickerProps {
  readonly recurrence: RecurrenceRule | null;
  readonly onChange: (rule: RecurrenceRule | null) => void;
  readonly eventDate?: string;
}

function getEventDayOfWeek(eventDate?: string): number {
  if (!eventDate) return new Date().getDay();
  return new Date(eventDate + "T00:00:00").getDay();
}

export function RecurrencePicker({
  recurrence,
  onChange,
  eventDate,
}: RecurrencePickerProps) {
  const [typeVal, setTypeVal] = useState<
    "none" | "daily" | "weekly" | "monthly" | "quarterly" | "semiannual" | "yearly"
  >(recurrence?.type ?? "none");
  const [showMore, setShowMore] = useState(
    ["quarterly", "semiannual", "yearly"].includes(recurrence?.type ?? "none"),
  );
  const [interval, setInterval] = useState(recurrence?.interval ?? 1);
  const [endDate, setEndDate] = useState<string | null>(
    recurrence?.endDate ?? null,
  );
  const [daysOfWeek, setDaysOfWeek] = useState<readonly number[]>(
    recurrence?.daysOfWeek ?? [getEventDayOfWeek(eventDate)],
  );

  function emit(
    type: "daily" | "weekly" | "monthly" | "quarterly" | "semiannual" | "yearly",
    newInterval: number,
    newEndDate: string | null,
    newDays: readonly number[],
  ) {
    const rule: RecurrenceRule =
      type === "weekly" && newDays.length > 0
        ? { type, interval: newInterval, endDate: newEndDate, daysOfWeek: newDays }
        : { type, interval: newInterval, endDate: newEndDate };
    onChange(rule);
  }

  function handleTypeChange(newType: string) {
    const type = newType as "none" | "daily" | "weekly" | "monthly" | "quarterly" | "semiannual" | "yearly";
    setTypeVal(type);
    if (type === "none") {
      onChange(null);
    } else {
      if (type === "weekly") {
        const days = daysOfWeek.length > 0 ? daysOfWeek : [getEventDayOfWeek(eventDate)];
        setDaysOfWeek(days);
        emit(type, interval, endDate, days);
      } else {
        emit(type, interval, endDate, daysOfWeek);
      }
    }
  }

  function handleIntervalChange(newInterval: number) {
    setInterval(newInterval);
    if (typeVal !== "none") {
      emit(typeVal, newInterval, endDate, daysOfWeek);
    }
  }

  function handleEndDateChange(newEndDate: string) {
    setEndDate(newEndDate || null);
    if (typeVal !== "none") {
      emit(typeVal, interval, newEndDate || null, daysOfWeek);
    }
  }

  function handleDayToggle(day: number) {
    const isSelected = daysOfWeek.includes(day);
    let newDays: readonly number[];
    if (isSelected && daysOfWeek.length > 1) {
      newDays = daysOfWeek.filter((d) => d !== day);
    } else if (!isSelected) {
      newDays = [...daysOfWeek, day].sort((a, b) => a - b);
    } else {
      return;
    }
    setDaysOfWeek(newDays);
    emit("weekly", interval, endDate, newDays);
  }

  return (
    <div>
      <label className="block text-[10px] font-medium text-ink-faint uppercase tracking-wide mb-1">
        Récurrence
      </label>
      <CustomSelect
        value={typeVal}
        onChange={handleTypeChange}
        dropUp
        options={[
          { value: "none", label: "Aucune" },
          { value: "daily", label: "Quotidienne" },
          { value: "weekly", label: "Hebdomadaire" },
          { value: "monthly", label: "Mensuelle" },
          ...(showMore ? [
            { value: "quarterly", label: "Trimestrielle" },
            { value: "semiannual", label: "Semestrielle" },
            { value: "yearly", label: "Annuelle" },
          ] : []),
        ]}
        className="w-full"
        footer={
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowMore((v) => !v); }}
            className="w-full text-left px-3 py-1.5 text-[10px] text-ink-faint hover:text-ink-muted transition-colors border-t border-border/40"
          >
            {showMore ? "− Moins d'options" : "+ Plus d'options"}
          </button>
        }
      />

      {typeVal !== "none" && (
        <>
          {typeVal === "weekly" && (
            <div className="mt-2">
              <label className="block text-[10px] font-medium text-ink-faint uppercase tracking-wide mb-1">
                Jours
              </label>
              <div className="flex gap-1.5">
                {DAY_LABELS.map((label, index) => {
                  const isActive = daysOfWeek.includes(index);
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleDayToggle(index)}
                      className={`w-7 h-7 rounded-full text-[11px] font-medium transition-colors
                        ${isActive
                          ? "bg-accent text-white"
                          : "bg-surface text-ink-muted border border-border/60 hover:border-accent/50"
                        }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <label className="block text-[10px] font-medium text-ink-faint uppercase tracking-wide mb-1">
                Intervalle
              </label>
              <input
                type="number"
                min="1"
                value={interval}
                onChange={(e) =>
                  handleIntervalChange(parseInt(e.target.value, 10))
                }
                className="w-full px-2.5 py-1.5 text-xs text-ink bg-surface border border-border/60 rounded-md
                  focus:outline-none focus:border-accent/60"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-ink-faint uppercase tracking-wide mb-1">
                Date fin
              </label>
              <input
                type="date"
                min={getToday()}
                value={endDate ?? ""}
                onChange={(e) => handleEndDateChange(e.target.value)}
                className="w-full px-2.5 py-1.5 text-xs text-ink bg-surface border border-border/60 rounded-md
                  focus:outline-none focus:border-accent/60"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
